import { useEffect, useId, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useT } from '@/i18n';
import { ChevronDown, User } from '@/components/ui';
import { cx } from '@/components/ui/cx';
import { LanguageToggle } from './LanguageToggle';

export interface AccountMenuLink {
  to: string;
  label: string;
}

/**
 * The desktop "More" control: a labelled button that opens a small panel under it with the
 * destinations the top bar has no room for, the language, and sign out. A disclosure, not a modal —
 * it is next to the button in tab order, closes on Escape (focus returns to the button), on a
 * click or focus outside it, and after a link is chosen. Below the `nav` breakpoint the header
 * shows the hamburger sheet instead.
 */
export const AccountMenu = ({ name, links, onSignOut }: { name?: string; links: AccountMenuLink[]; onSignOut: () => void }) => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const initial = (name ?? '').trim().charAt(0).toUpperCase();

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      className="relative"
      onBlur={(e) => {
        if (open && !e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        className={cx(
          'inline-flex min-h-tap items-center gap-2 rounded-pill border bg-white py-1 pl-1.5 pr-3 text-label font-bold text-ink transition-colors',
          open ? 'border-lagoon-600' : 'border-sage-300 hover:border-lagoon-600 hover:bg-mist',
        )}
      >
        <span aria-hidden="true" className="flex h-8 w-8 items-center justify-center rounded-full bg-lagoon-100 font-display text-[15px] text-lagoon-700">
          {initial || <User className="h-[18px] w-[18px]" />}
        </span>
        {t('nav.more')}
        <ChevronDown className={cx('h-4 w-4 text-ink-muted transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div id={panelId} className="absolute right-0 top-full z-40 mt-2 w-64 rounded-lg border border-line bg-white p-2 shadow-2">
          <nav aria-label={t('nav.more')} className="flex flex-col">
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  cx(
                    'flex min-h-tap items-center rounded-md px-3 text-label font-bold transition-colors',
                    isActive ? 'bg-lagoon-100 text-lagoon-700' : 'text-ink hover:bg-mist hover:text-lagoon-600',
                  )
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
          <div className="my-2 border-t border-line" />
          <div className="flex items-center justify-between gap-3 px-3 py-1">
            <span className="text-label font-bold text-ink-soft">{t('nav.language')}</span>
            <LanguageToggle />
          </div>
          <div className="my-2 border-t border-line" />
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
            className="flex min-h-tap w-full items-center rounded-md px-3 text-left text-label font-bold text-ink-soft transition-colors hover:bg-mist hover:text-lagoon-600"
          >
            {t('nav.signout')}
          </button>
        </div>
      )}
    </div>
  );
};
